import type { Answer, AnswerValue, Question, Survey } from "./contract";
import { normalizeDate } from "./date";
import { effectiveVisibleIds } from "./logic";
import {
  isBlankAfterStrip,
  stripHallucinationSpans,
} from "../lib/transcript/hallucination";

/**
 * 音声回答 (自由/対話モード) の AI 抽出エンジン (PRODUCT_DEV-574)。
 *
 * 発話の文字起こし(transcript)から、調査スキーマの各設問へ値を割り当てる。
 * LLM プロバイダ非依存の **純ロジック** (プロンプト生成 + 応答パース + スキーマ検証)。
 * 実際の呼び出しは survey Lambda が OpenAI (Unizo と同じ経路) 経由で行う。
 */

const questionSpec = (q: Question): string => {
  const base = `- questionId="${q.questionId}" (${q.type}) 「${q.label}」${q.required ? " [必須]" : ""}${q.phase !== "always" ? ` [表示条件: ${q.phase}]` : ""}`;
  if (q.type === "radio")
    return `${base}\n    選択肢: ${q.options.map((o) => `${o.v}=「${o.label}」${o.withText ? "(自由記入可)" : ""}`).join(" / ")}\n    値: 選んだ選択肢の v 文字列。自由記入付きは {"v":"<v>","text":"<内容>"}`;
  if (q.type === "checkbox")
    return `${base}\n    選択肢: ${q.options.map((o) => `${o.v}=「${o.label}」`).join(" / ")}\n    値: 該当する v の配列 ["v1","v2"]`;
  if (q.type === "date") return `${base}\n    値: "YYYY/MM/DD" 文字列`;
  if (q.type === "scale") return `${base}\n    値: ${q.scaleMin}〜${q.scaleMax} の数値`;
  return `${base}\n    値: フリーテキスト文字列`;
};

/** 文字起こし→回答抽出の system prompt。JSON {answers:[{questionId,value}]} を要求する。 */
export const buildExtractionSystemPrompt = (survey: Survey): string =>
  [
    "あなたは営業担当の発話を、アンケートの各設問の回答に構造化するアシスタントです。",
    `調査名: ${survey.title}`,
    "以下の設問定義に従い、発話内容から読み取れる回答だけを抽出してください。",
    "読み取れない/言及されていない設問は含めないでください（推測で埋めない）。",
    "発話記録に「AI:」「回答者:」のラベルが付いている場合、回答は回答者の発話のみから抽出し、AI の発話は質問の文脈としてだけ使ってください。",
    "",
    "設問定義:",
    ...survey.questions.map(questionSpec),
    "",
    '出力は必ず次の JSON のみ: {"answers":[{"questionId":"...","value":<値>}]}',
    "value の形式は各設問の指定に厳密に従うこと。前置き・説明・コードフェンスは出力しない。",
    '自由記入可の選択肢（「その他」等）が選ばれている場合は、必ず {"v":"<v>","text":"<発話された具体的内容>"} のオブジェクト形式で返すこと（text を省略しない）。',
  ].join("\n");

// ── 音声入力 (ASR) チューニング (バッチ8) ────────────────────────────────
// Unizo (商談録音) が精度のために積んでいる設定を survey にも揃える。値の正本はここ。
// Realtime は mint (survey-voice.ts) と session.update (useRealtimeVoice.ts) の両方で、
// 自由モードは /transcribe の Whisper 系 API で使う。

/**
 * 文字起こしモデル。whisper-1 より日本語精度が高い (Unizo の whisperTranscribe と同じ)。
 * env での差し替え口は設けない — Realtime は web の session.update でも同じ値を送るため、
 * サーバだけ env で変えると dialog(web 値) と freerec(env 値) でモデルが分裂する。
 */
export const TRANSCRIBE_MODEL = "gpt-4o-transcribe";

/**
 * Realtime の発話区切り検出 (VAD)。未指定だと OpenAI 既定任せになり語尾切れが起きる。
 * 値は Unizo の RealtimeConfig 既定 (server_vad / 0.5 / 500ms / 300ms) と同一。
 */
export const REALTIME_TURN_DETECTION = {
  type: "server_vad",
  threshold: 0.5,
  silence_duration_ms: 500,
  prefix_padding_ms: 300,
  interrupt_response: true,
  create_response: true,
} as const;

/**
 * 語彙ヒントの上限 (経験値)。ASR の prompt は長いほど効きが薄まるため、設問が多い調査では
 * 収まる範囲の語彙だけを載せる (budget を使い切るまで詰める)。
 */
const VOCAB_PROMPT_MAX_CHARS = 400;

/** 調査スキーマに出てくる語彙 (調査名・設問ラベル・選択肢ラベル)。重複は除く。 */
const surveyVocabulary = (survey: Survey): string[] => {
  const words: string[] = [survey.title];
  for (const q of survey.questions) {
    words.push(q.label);
    if (q.type === "radio" || q.type === "checkbox")
      words.push(...q.options.map((o) => o.label));
    if (q.type === "scale") {
      if (q.minLabel) words.push(q.minLabel);
      if (q.maxLabel) words.push(q.maxLabel);
    }
  }
  return [...new Set(words.map((w) => w.trim()).filter(Boolean))];
};

/**
 * 文字起こし(ASR)への言語・語彙ヒント。
 * Unizo はテナント辞書を注入するが、survey は辞書を持たない代わりに
 * **調査スキーマ自体が語彙表**なので、そこから固有語のヒントを組み立てる。
 */
export const buildTranscriptionPrompt = (survey: Survey): string => {
  const head =
    "日本語のアンケート回答です。日本語以外の発話は文字起こししないでください。";
  let vocab = "";
  for (const w of surveyVocabulary(survey)) {
    const next = vocab ? `${vocab} / ${w}` : w;
    // 長い語で打ち切らず、budget に収まる後続の語は拾う。
    if (next.length > VOCAB_PROMPT_MAX_CHARS) continue;
    vocab = next;
  }
  return vocab ? `${head}\n次の語彙が出てきます: ${vocab}` : head;
};

/** Realtime セッションの audio.input 設定 (文字起こし + VAD + ノイズ抑制)。 */
export interface AudioInputConfig extends Record<string, unknown> {
  transcription: { model: string; language: string; prompt?: string };
  turn_detection: typeof REALTIME_TURN_DETECTION;
  noise_reduction: { type: string };
}

/**
 * audio.input の**唯一の定義箇所**。mint (Lambda) と session.update (web) の両方がこれを使う。
 * survey 未取得時は語彙ヒントだけ落とし、VAD/ノイズ抑制は必ず適用する
 * (フォールバックで無設定に落ちると精度チューニングが黙って外れるため)。
 */
export const buildAudioInputConfig = (survey?: Survey): AudioInputConfig => ({
  transcription: {
    model: TRANSCRIBE_MODEL,
    language: "ja",
    ...(survey ? { prompt: buildTranscriptionPrompt(survey) } : {}),
  },
  turn_detection: { ...REALTIME_TURN_DETECTION },
  noise_reduction: { type: "near_field" },
});

/**
 * 接続後の session.update で再適用する audio.input (web 用)。
 * `noise_reduction` は **mint 時のみ**送る (Unizo も session.update では送っていない未検証キーで、
 * GA が拒否すると session.update 全体が 400 になり instructions/tools まで無効化されるため)。
 */
export const buildSessionUpdateAudioInput = (
  survey?: Survey,
): Omit<AudioInputConfig, "noise_reduction"> => {
  const { transcription, turn_detection } = buildAudioInputConfig(survey);
  return { transcription, turn_detection };
};

/**
 * ASR が無音区間で吐く定型ハルシネーションを決定論的に除去する (LLM 不使用)。
 *
 * PRODUCT_DEV-776: パターン表と除去ロジックは `lib/transcript/hallucination` が正本
 * (それまでは unizo 側と二重管理で「増えたら両方に足す」運用だった)。
 * ここは survey 固有の整形 —— 行の trim と空行の除去 —— だけを担う。
 */
export const stripHallucinations = (text: string): string =>
  text
    .split(/\n+/)
    .map((line) => stripHallucinationSpans(line).trim())
    .filter((line) => line !== "" && !isBlankAfterStrip(line))
    .join("\n")
    .trim();

/**
 * 対話モードの完了フレーズ。AI がこれを発話したらクライアント (useRealtimeVoice) が
 * 自動終了→抽出へ進む。instructions とクライアント検知の両方で使う正本。
 */
export const DIALOGUE_END_PHRASE = "以上で完了です";

/** 対話モード instructions のオプション (③: 事前充填済みの設問は質問させない)。 */
export interface DialogueInstructionOpts {
  /** 既に確定している回答 (例: 個人顧客のヒアリング相手)。AI はこれらを質問しない。 */
  knownAnswers?: { label: string; value: string }[];
}

/**
 * 対話モード: AI が1問ずつ日本語で質問するための instructions。
 * ④: 定義外の質問・復唱の禁止を明示。⑤: record_answer ツールの使用規律を明示
 * (tools 本体は buildDialogueTools。サーバ mint 時は opts なしで呼ばれ、クライアントが
 * session.update で knownAnswers 込みに上書きする)。
 */
export const buildDialogueInstructions = (
  survey: Survey,
  opts?: DialogueInstructionOpts,
): string =>
  [
    `あなたはアンケート「${survey.title}」を営業担当に音声で実施するアシスタントです。`,
    `会話の最初に必ず「${survey.title}について回答をお願いします。まずは…」のように調査名を述べてから、最初の設問を尋ねてください。`,
    "以下に定義された設問だけを、上から順に1つずつ、簡潔な日本語で質問してください。",
    "【禁止事項】設問定義にない質問・雑談・深掘り質問・言い換えの確認質問は一切しないでください。",
    "【禁止事項】回答内容の復唱・反復・要約をしないでください。回答を受け取ったら「ありがとうございます」程度の短い相槌のみで、すぐ次の設問に進みます（回答者が訂正したときだけ、訂正後の内容を一度だけ確認してよい）。",
    "回答者が設問への回答を発話したら、その都度必ず record_answer ツールを呼び出して記録してください（今回の発話で得られた設問のみを含める）。",
    "ツール結果の collected にある設問は記録済みです。絶対に聞き返さず、remaining の先頭の設問だけを次に質問してください。",
    "選択式は選択肢を口頭で提示し、必須項目が埋まるまで丁寧に聞き取ってください。",
    "自由記入可の選択肢（「その他」等）が選ばれた場合は、続けてその具体的な内容を必ず質問し、聞き取ってください。",
    "各設問の [表示条件: 親questionId:値] は分岐条件です。その親questionIdの設問の回答が指定の値（| 区切りはいずれか）のときだけ、その設問を質問してください。条件が && で複数連結されている場合は、すべての条件を満たすときだけ質問します。条件を満たさない設問は飛ばします。",
    ...(opts?.knownAnswers?.length
      ? [
          "次の設問は既に回答が確定しています。質問せず、record_answer にも含めないでください:",
          ...opts.knownAnswers.map((k) => `- 「${k.label}」= ${k.value}`),
        ]
      : []),
    `remaining が空になったら（すべて聞き終えたら）、お礼を述べてから必ず「${DIALOGUE_END_PHRASE}」という言葉で締めくくってください（例:「ご回答いただきありがとうございました。${DIALOGUE_END_PHRASE}」）。この言葉は完了時以外には使わないでください。`,
    "",
    "設問定義（この順序・この項目のみを聞く）:",
    ...survey.questions.map(questionSpec),
  ].join("\n");

// ── 対話モードの function calling (⑤・Unizo extract_meeting_report 踏襲) ────
export const RECORD_ANSWER_TOOL = "record_answer";

/** record_answer の per-question パラメータスキーマ (OpenAI function parameters 形式)。 */
const toolParamSpec = (q: Question, order: number): Record<string, unknown> => {
  const desc = `(質問順:${order}) ${q.label}${q.required ? " [必須]" : ""}`;
  switch (q.type) {
    case "radio":
      return {
        type: "string",
        enum: q.options.map((o) => o.v),
        description: `${desc}。選択肢: ${q.options.map((o) => `${o.v}=「${o.label}」`).join(" / ")}${q.options.some((o) => o.withText) ? "。自由記入付き選択肢の具体内容は別途発話で聞き取る" : ""}`,
      };
    case "checkbox":
      return {
        type: "array",
        items: { type: "string", enum: q.options.map((o) => o.v) },
        description: `${desc}。該当する選択肢の v をすべて。選択肢: ${q.options.map((o) => `${o.v}=「${o.label}」`).join(" / ")}`,
      };
    case "scale":
      return { type: "number", description: `${desc}。${q.scaleMin}〜${q.scaleMax} の数値` };
    case "date":
      return { type: "string", description: `${desc}。"YYYY/MM/DD" 文字列` };
    default:
      return { type: "string", description: `${desc}。フリーテキスト` };
  }
};

/**
 * 対話モードの Realtime tools 定義 (session.update で注入)。
 * 単一ツール record_answer に全設問を optional プロパティで持たせ、
 * 「回答が得られた設問だけを含めて毎回呼ぶ」運用 (Unizo generateToolDefinitions と同形)。
 */
export const buildDialogueTools = (survey: Survey): Record<string, unknown>[] => [
  {
    type: "function",
    name: RECORD_ANSWER_TOOL,
    description:
      "回答者がアンケート設問に回答したら、その都度即座に呼び出して記録する。今回の発話で得られた設問のみをパラメータに含める（未回答の設問は含めない）。",
    parameters: {
      type: "object",
      properties: Object.fromEntries(
        survey.questions.map((q, i) => [q.questionId, toolParamSpec(q, i + 1)]),
      ),
      additionalProperties: false,
    },
  },
];

/**
 * record_answer のツール引数を検証済み Answer[] に変換する (不正値・不明 questionId は破棄)。
 * radio の自由記入 text は対話の発話側で拾う (抽出の safety-net が補完) ため v のみ受ける。
 */
export const coerceToolAnswers = (
  survey: Survey,
  args: Record<string, unknown>,
): Answer[] => {
  const byId = new Map(survey.questions.map((q) => [q.questionId, q]));
  const out: Answer[] = [];
  for (const [questionId, raw] of Object.entries(args)) {
    const q = byId.get(questionId);
    if (!q) continue;
    const value = coerce(q, raw);
    if (value !== undefined) out.push({ questionId, value });
  }
  return out;
};

/**
 * 対話の進捗 (回答済み/未回答の設問ラベル)。record_answer の tool output として返し、
 * 「collected は聞き返さず remaining の先頭だけ質問」の規律をモデルに強制する。
 * 表示条件 (phase) を尊重し、分岐で非表示の設問は remaining に含めない。
 */
export const computeSurveyProgress = (
  survey: Survey,
  byId: Map<string, AnswerValue>,
): { collected: string[]; remaining: string[] } => {
  const visible = effectiveVisibleIds(survey.questions, byId);
  const collected: string[] = [];
  const remaining: string[] = [];
  for (const q of survey.questions) {
    if (!visible.has(q.questionId)) continue;
    const v = byId.get(q.questionId);
    const answered =
      v !== undefined &&
      v !== null &&
      v !== "" &&
      !(Array.isArray(v) && v.length === 0);
    (answered ? collected : remaining).push(q.label);
  }
  return { collected, remaining };
};

const coerce = (q: Question, raw: unknown): AnswerValue | undefined => {
  switch (q.type) {
    case "radio": {
      if (typeof raw === "string") return q.options.some((o) => o.v === raw) ? raw : undefined;
      if (raw && typeof raw === "object" && "v" in raw) {
        const v = String((raw as { v: unknown }).v);
        const text = "text" in raw ? String((raw as { text: unknown }).text ?? "") : "";
        return q.options.some((o) => o.v === v) ? { v, text } : undefined;
      }
      return undefined;
    }
    case "checkbox": {
      if (!Array.isArray(raw)) return undefined;
      const vs = raw.map(String).filter((x) => q.options.some((o) => o.v === x));
      return vs.length ? vs : undefined;
    }
    // AI は "2026年7月1日" のような表記も返すため正本形式へ正規化する (不正・非実在日は未回答)。
    case "date":
      return typeof raw === "string" ? normalizeDate(raw) : undefined;
    case "text":
      return typeof raw === "string" && raw.trim() !== "" ? raw : undefined;
    case "scale": {
      // 非数値 (null/""/空白/配列) を 0 に化かさない (Number(null)===0 対策)。
      const n =
        typeof raw === "number"
          ? raw
          : typeof raw === "string" && raw.trim() !== ""
            ? Number(raw)
            : NaN;
      return Number.isFinite(n) && n >= q.scaleMin && n <= q.scaleMax ? n : undefined;
    }
  }
};

/**
 * LLM の JSON 応答をスキーマ検証して Answer[] に変換する。
 * 型不一致・範囲外・不明 questionId は破棄する (推測混入を防ぐ)。
 */
export const parseExtractedAnswers = (
  rawJson: string,
  survey: Survey,
): Answer[] => {
  let obj: unknown;
  try {
    const s = rawJson.indexOf("{");
    const e = rawJson.lastIndexOf("}");
    obj = JSON.parse(s === -1 || e < s ? rawJson : rawJson.slice(s, e + 1));
  } catch {
    return [];
  }
  const list = (obj as { answers?: unknown }).answers;
  if (!Array.isArray(list)) return [];
  const byId = new Map(survey.questions.map((q) => [q.questionId, q]));
  const out: Answer[] = [];
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const questionId = String((item as { questionId?: unknown }).questionId ?? "");
    const q = byId.get(questionId);
    if (!q) continue;
    const value = coerce(q, (item as { value?: unknown }).value);
    if (value !== undefined) out.push({ questionId, value });
  }
  return out;
};
